/**
 * components/TransactionDetailModal.js
 * Read-only "Detail Transaksi" (PRD §5.1, amended; type-generalized in T1c for
 * Pembelian/Transfer) — full transaction info plus the complete timeline since creation via
 * EditLogTimeline.
 *
 * Phase T2b: the "No. Surat Jalan Cabang" row was removed (dead field since T2a — a cabang
 * surat jalan shares the invoice's own minted nota number instead). A minimal per-payment list
 * (method + amount) was added under the Total/Sisa card — without it, a split payment entered
 * at creation could never be read back anywhere in the app until T3.
 *
 * Phase T2b-fix: each row's method text goes through `utils/payments.js`'s shared
 * `paymentMethodText` — a LAINNYA row shows just its own note (e.g. "Alipay"), never the
 * redundant "Lainnya — Alipay". Don't reimplement that display rule here.
 *
 * Payment-correction rework: the payment list below is plain read-only text, matching every
 * other field in this modal — correcting/adding/removing a payment record now happens
 * exclusively via Edit Transaksi (`TransactionFormModal`, edit mode). This modal no longer
 * takes an `onCorrectPayment` prop.
 *
 * Payment-correction rework fix: each row now also shows its own `fmtDate(p.paidAt)` timestamp
 * (e.g. "Tunai — 08 Agu 2026, 21.09 — Rp 500.000") — without it, two payments sharing a
 * method were indistinguishable in this list. Reuses the same `fmtDate` formatter
 * `EditLogTimeline.js` already uses for `txn.createdAt`/`entry.at`, not a second formatter.
 *
 * paidAt feature: `paidAt` is a NEW field, a user-settable real-world payment date/time,
 * separate from `at` (which stays a pure server-stamped audit/insertion timestamp — unchanged,
 * never shown, never editable). This line switched from `fmtDate(p.at)` to `fmtDate(p.paidAt)`
 * accordingly — correctable via Edit Transaksi's own payment-rows editor, same as
 * amount/method/methodNote.
 *
 * Cost items Brief 2: a "Total Dikurangi Biaya" row — `total - Σbiaya-type costItems`
 * (tagihan/tagihanSupplier is already baked into `total`, so it's not subtracted again here) —
 * renders inside the money-summary card, shown ONLY when it would actually differ from Total
 * Nilai (i.e. only once at least one biaya item exists) — matches this card's own existing
 * convention of hiding a derived line that wouldn't add information (Sisa Tagihan already
 * hides itself once Lunas). Corrective pass (Piece 1): moved from directly under Total Nilai
 * to the END of the money-summary card (after Sudah Dibayar/Sisa Tagihan/payments, behind its
 * own divider) — the money-summary card should read Total -> Dibayar -> Sisa/pembayaran first,
 * with this secondary derived figure trailing. Its own `color: var(--muted)` styling is
 * untouched. This pass also restores two sections that were part of the original design but
 * got dropped when Brief 2 was scoped: a "Subtotal Barang" card (the goods-only total, via
 * `goodsTotal`) and a "Biaya & Tagihan" itemized list card (the raw costItems rows) — both
 * shown ONLY when `txn.costItems` is non-empty, rendered directly above the money-summary card.
 * A transaction with no cost items renders exactly as it always has.
 *
 * Corrective pass, Piece 2 (Potongan): the restored itemized card is renamed "Rincian Tambahan"
 * (was "Biaya & Tagihan") — a Potongan row rendering there tagged "Potongan" would make the old
 * title flatly inaccurate, same reasoning as the earlier "+Biaya" -> "+Rincian" list-badge
 * rename; a generic name so it never needs editing again for a future type. The money-summary
 * card's end-of-card derived row now branches by type: PENJUALAN/TRANSFER keep "Total Dikurangi
 * Biaya" (`totalDikurangiBiaya`, unchanged formula/visibility rule) — PEMBELIAN gets a NEW
 * "Total Ditambah Biaya" (`totalDitambahBiaya`) instead, since Pembelian's money only ever
 * flows OUT and Biaya there is a second outflow to add, not net against. Same visibility rule
 * (shown only once a biaya item exists), same divider, same end-of-card position.
 *
 * Tagihan & Potongan rename task: the single "Rincian Tambahan" card above (all 3 cost-item
 * types, unfiltered, no color) is REPLACED by two independently-gated boxes, mirroring the
 * left-to-right money story the dashboard row already tells (see TxnCostCells.js): a new
 * "Tagihan & Potongan" box — filtered to tagihan/tagihanSupplier/potongan only (Biaya moved
 * out), each item colored by type and a new aggregate net-total line colored by sign, both via
 * the shared `costItemColor`/`costItemNetColor` helpers (utils/costItems.js) extracted from
 * TxnCostCells.js's own RincianTambahanCell for exactly this reuse — and a new "Total Setelah
 * Biaya" box, holding the Biaya-only items (no color/chip, box is already scoped) plus a
 * genuinely NEW "Total Biaya" line (`sumBiayaCostItems`, never displayed on its own before) and
 * the pre-existing Dikurangi/Ditambah Biaya final line, relocated here from its old trailing
 * position inside the money-summary card. That box's gate is deliberately `hasBiayaItems`
 * (whether any Biaya row exists), NOT the old `dikurangiBiaya !== total` check — a Biaya row
 * with `amount: 0` now renders the box (Total Biaya shows 0), which the old gate would have
 * hidden; a deliberate behavior change, not an oversight. Subtotal Barang keeps its own
 * original `hasCostItems` gate untouched. The money-summary card's own Total Nilai number is
 * now colored via `TOTAL_COLOR_BY_TYPE` (reused from TxnNilaiCell.js, not re-derived) — every
 * other line in that card (Sudah Dibayar/Sisa Tagihan/payments) is unchanged.
 *
 * Biaya color hierarchy fix: "Total Setelah Biaya" originally rendered all three of its
 * elements (Biaya item rows, the "Total Biaya" line, the final Dikurangi/Ditambah Biaya line)
 * at effectively the same visual weight, making them hard to tell apart. Now a real three-tier
 * hierarchy: Biaya item rows are a muted gray (originally `var(--muted)`, the SAME token
 * `.subtotal-cell__items` (styles.css, TxnCostCells.js's dashboard cell) already uses for its
 * own muted item-breakdown lines — reused as a VALUE, not as that literal class, since the
 * class also bundles dashboard-table-cell-specific layout that would break this box's existing
 * two-column flex-row lines); "Total Biaya" is plain regular-weight black (no color/weight
 * style at all — the middle tier); the final Dikurangi/Ditambah Biaya line is bold black
 * (`<b>`, no color) — matching the bold-black convention every other final derived total in
 * this modal already uses (e.g. Subtotal Barang's own total).
 *
 * Biaya item-name color legibility fix: `var(--muted)` (#64748b, Tailwind's slate-500) read too
 * light/low-contrast for the item NAME specifically. No existing token sits between `--muted`
 * (#64748b) and `--text` (#1e293b, the near-black every plain/bold line in this app renders
 * in) — checked every `:root` token in `styles.css` before proposing a new value, and every
 * other genuinely darker-but-still-secondary gray used anywhere in this codebase turned out to
 * be a one-off literal hex (e.g. the amber `#92400e` family), never a reusable token, so a new
 * literal value here matches this codebase's own existing precedent rather than breaking it.
 * `#475569` (Tailwind's slate-600, one step darker than `--muted`'s slate-500 in the SAME
 * palette family this app's whole gray scale already derives from) replaces `var(--muted)` for
 * ONLY this one row — scoped to this box alone, nothing else in this modal or the app changes.
 * Still genuinely lighter/less prominent than the plain-black "Total Biaya" line directly below
 * it (`#475569` vs. the ambient `#1e293b`/near-black text color), so the three-tier hierarchy
 * above holds; just legible enough to read comfortably against this card's white background
 * (contrast ratio ~7.5:1, vs. `--muted`'s own ~4.4:1).
 *
 * **Pembelian batch 2 (transfer support) — a new optional `perspective` prop, needed once the
 * SAME transfer can be opened from either Penjualan or Pembelian.** Two, and only two, things
 * change with it; every other `isPembelian`-gated branch below (the invoice-field styling, the
 * Sales-row visibility, the buyer-block visibility) stays keyed on the real `txn.type` —
 * they describe the transaction itself, not who's currently looking at it, and must not follow
 * the viewer:
 *   1. The Ditambah/Ditambah Biaya wording (`isPembelianView`, mirrors `TxnCostCells.js`'s own
 *      identical fix on `TotalSetelahBiayaCell` — same reasoning, not repeated here).
 *   2. **Pihak, for a TRANSFER only.** `contact` (`txn.contactId`) always resolves to the
 *      DESTINATION side's own internal stand-in, by construction (`App.js`'s
 *      `resolveTransferContact` — confirmed against `utils/piutangUtang.js`'s own
 *      `transferStatusFor` header comment) — correct as Pihak when viewed from the SOURCE's own
 *      books (Penjualan: Pihak = the counterparty = destination), but wrong the moment the same
 *      row is opened from the DESTINATION's own books (Pembelian): Pihak there must name the
 *      SOURCE instead, which isn't stored as a contact at all — it's just `txn.location`/
 *      `txn.branchId` read directly, the exact expression `utils/branches.js`'s
 *      `resolveLocationLabel` now names. `perspective==="PEMBELIAN"` switches to that; anything
 *      else (including omitted, PiutangUtang.js's own call site) keeps today's `displayContact`
 *      read, unchanged.
 * `perspective: "PENJUALAN"|"PEMBELIAN"|undefined` — Penjualan.js/Pembelian.js pass it
 * explicitly now; PiutangUtang.js's own call omits it, preserving old behavior byte-for-byte
 * (that page's own whole-business PIUTANG/UTANG convention for TRANSFER is a separate, deferred
 * inconsistency — see root CLAUDE.md).
 */
import React from "react";
import { fmtQty, fmtDate, fmtDelta } from "../utils/idGenerators";
import { bucketLabel } from "../utils/transformations";
import { txnTotal, txnSisa, txnStatus, dueState, pctPaid, totalDikurangiBiaya, totalDitambahBiaya, goodsTotal } from "../utils/txnStatus";
import { costItemTypeLabel, sumCostItemsRaisingTotal, sumPotonganCostItems, sumBiayaCostItems, costItemColor, costItemNetColor } from "../utils/costItems";
import { hasTagihanPotonganItems, shouldShowBucketBars, barangSisa, barangPctPaid, tagihanPotonganSisa, tagihanPotonganPctPaid, paymentSplitLine } from "../utils/paymentSplit";
import { resolveBranchName, resolveLocationLabel } from "../utils/branches";
import { displayContact } from "../utils/contacts";
import { resolveSalespersonName, resolveSalespersonCode } from "../utils/salespeople";
import { resolvePersonNote } from "../utils/persons";
import { paymentMethodText } from "../utils/payments";
import { useModalEsc, backdropClose } from "../utils/modalStack";
import { StatusPill, DueBadge } from "./TxnBadges";
import { TOTAL_COLOR_BY_TYPE, PaymentProgress } from "./TxnNilaiCell";
import EditLogTimeline from "./EditLogTimeline";
import { useExpenses } from "./ExpensesContext";
import { expensesForTxn, sumExpenses, expenseLabel } from "../utils/expenses";

const TYPE_LABEL = { PENJUALAN: "Penjualan", PEMBELIAN: "Pembelian", TRANSFER: "Transfer" };
const DIRECTION_LABEL = { PUSAT_TO_CABANG: "Pusat → Cabang", CABANG_TO_PUSAT: "Cabang → Pusat" };

export default function TransactionDetailModal({ txn, items, contacts, salespeople, persons = [], branches = [], today, perspective, onClose }) {
  useModalEsc(onClose);
  // Biaya Lain-Lain (2026-10-11): this transaction's own costs (`data.expenses`, via
  // ExpensesContext). Shown read-only — they're changed in Edit Transaksi, their one surface.
  const { expenses: allExpenses } = useExpenses();
  const linkedExpenses = expensesForTxn(allExpenses, txn.id);
  const expenseTotal = sumExpenses(linkedExpenses);

  const isPembelian = txn.type === "PEMBELIAN";
  const isTransfer = txn.type === "TRANSFER";
  // Pembelian batch 2 — see this file's own header comment. Only the Ditambah/Dikurangi Biaya
  // wording follows the viewing page; every other `isPembelian` use above stays type-based.
  const isPembelianView = perspective ? perspective === "PEMBELIAN" : isPembelian;

  const byCode = new Map(items.map((it) => [it.internalCode, it]));
  const contact = contacts.find((c) => c.id === txn.contactId) || null;
  // Contact model redesign leak fix: a TRANSFER's `contact` is always an app-maintained internal
  // stand-in whose `.name` is a fixed, never-real placeholder ("__internal__") — reading it
  // directly would show that literal string as "Pihak". `displayContact` resolves the real
  // branch name instead (or "" when it genuinely can't — never the raw field).
  // Pembelian batch 2 — `contact` always names the DESTINATION side (see this file's own header
  // comment); viewed from Pembelian's own books (perspective === "PEMBELIAN"), Pihak must name
  // the SOURCE instead, read straight off txn.location/txn.branchId, never a contact.
  const contactName = isTransfer && perspective === "PEMBELIAN"
    ? resolveLocationLabel(txn.location, txn.branchId, branches)
    : (contact ? displayContact(contact, branches) : "");
  const salesperson = salespeople.find((s) => s.id === txn.salespersonId) || null;

  const total = txnTotal(txn);
  const sisa = txnSisa(txn);
  const paid = total - sisa;
  const status = txnStatus(txn);
  const due = dueState(txn, today);
  const dikurangiBiaya = totalDikurangiBiaya(txn, expenseTotal);
  const ditambahBiaya = totalDitambahBiaya(txn, expenseTotal);
  const costItems = txn.costItems || [];
  const hasCostItems = costItems.length > 0;
  const relevantCostItems = costItems.filter((c) => c.type === "tagihan" || c.type === "tagihanSupplier" || c.type === "potongan");
  const hasRelevantCostItems = relevantCostItems.length > 0;
  const costNet = sumCostItemsRaisingTotal(costItems) - sumPotonganCostItems(costItems);
  const biayaItems = [
    ...costItems.filter((c) => c.type === "biaya"),
    ...linkedExpenses.map((e) => ({ id: e.id, description: expenseLabel(e), amount: e.amount })),
  ];
  const hasBiayaItems = biayaItems.length > 0;
  const totalColor = TOTAL_COLOR_BY_TYPE[txn.type] || "var(--blue-deep)";

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
      <div className="modal-box modal-box--wide modal-box--detail" onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Tutup">×</button>
        <div className="modal-detail__head">
          <h2 className="modal-title">
            Transaksi {TYPE_LABEL[txn.type] || txn.type}
            {isTransfer && <span className="chip chip--transfer" style={{ marginLeft: "var(--gap-sm)" }}>TRANSFER</span>}
          </h2>
          {isPembelian ? (
            <div className="page-sub mono" style={{ color: "#555" }}>{txn.supplierInvoiceNo || "—"}</div>
          ) : (
            <div className="page-sub mono">{txn.invoiceNumber || "—"}</div>
          )}
        </div>

        <dl className="detail-grid">
          <dt>Tanggal &amp; Waktu</dt><dd>{txn.date} · {txn.time}</dd>
          {isTransfer ? (
            <><dt>Arah Transfer</dt><dd>{DIRECTION_LABEL[txn.transferDirection] || "—"}</dd></>
          ) : (
            <><dt>Lokasi</dt><dd><span className="chip chip--muted">
              {(txn.location === "CABANG" ? resolveBranchName(branches, txn.branchId) : null) || bucketLabel(txn.location)}
            </span></dd></>
          )}
          {!isPembelian && (<><dt>Sales</dt><dd>
            {salesperson ? (
              <>
                {resolveSalespersonName(persons, salesperson) || "—"} ({resolveSalespersonCode(persons, salesperson) || "—"})
                {(() => {
                  const note = resolvePersonNote(persons, salesperson.personId);
                  return note ? <span style={{ color: "var(--muted)" }}> — {note}</span> : null;
                })()}
                {salesperson.active === false && <span className="chip chip--muted" style={{ marginLeft: "var(--gap-sm)" }}>Nonaktif</span>}
              </>
            ) : "—"}
          </dd></>)}
          <dt>{isPembelian ? "Penjual" : isTransfer ? "Pihak" : "Pembeli"}</dt>
          <dd>{contactName || "—"}</dd>
          {/* Rename (manual-testing report, issue 3): `buyerBlock` is the name/address that
              PRINTS on the invoice/surat jalan, not a second copy of the Pembeli contact
              picked just above — see TransactionFormModal.js's own identical rename for the
              full reasoning. */}
          {!isPembelian && !isTransfer && (<>
            <dt>Nama Tercetak</dt><dd>{txn.buyerBlock?.name || "—"}</dd>
            <dt>Alamat Tercetak</dt><dd>{txn.buyerBlock?.address || "—"}</dd>
          </>)}
          <dt>Status</dt>
          <dd>
            <StatusPill status={status} />
            {due.kind !== "none" && <span style={{ marginLeft: "var(--gap-sm)" }}><DueBadge due={due} /></span>}
          </dd>
        </dl>

        <div className="table-wrap" style={{ marginTop: "var(--gap-lg)" }}>
          <table className="table">
            <thead>
              <tr><th>Kode</th><th>Nama</th><th className="num">Qty</th><th className="num">Harga</th><th className="num">Subtotal</th></tr>
            </thead>
            <tbody>
              {(txn.lines || []).map((l, i) => (
                <tr key={i} style={{ cursor: "default" }}>
                  <td className="mono">{l.internalCode}</td>
                  <td>{l.name || byCode.get(l.internalCode)?.name || "—"}</td>
                  <td className="num">{fmtQty(l.qty)}</td>
                  <td className="num">{fmtQty(l.unitPrice)}</td>
                  <td className="num">{fmtQty(l.qty * l.unitPrice)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {hasCostItems && (
          <div className="card" style={{ marginTop: "var(--gap-lg)" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Subtotal Barang</span><b>{fmtQty(goodsTotal(txn))}</b>
            </div>
            {shouldShowBucketBars(txn) && (
              <div className="detail-sisa-box">
                <div className="detail-sisa-box__sisa">Sisa: Rp {fmtQty(barangSisa(txn))}</div>
                <PaymentProgress pct={barangPctPaid(txn)} />
              </div>
            )}
          </div>
        )}

        {hasRelevantCostItems && (
          <div className="card" style={{ marginTop: "var(--gap-lg)" }}>
            <div className="card__title">Tagihan &amp; Potongan</div>
            {relevantCostItems.map((c) => (
              <div key={c.id} style={{ display: "flex", justifyContent: "space-between", marginBottom: "var(--gap-sm)" }}>
                <span>{c.description} <span className="chip chip--muted" style={{ marginLeft: "var(--gap-sm)" }}>{costItemTypeLabel(c.type, txn.type)}</span></span>
                <span style={{ color: costItemColor(c.type) }}>Rp {fmtQty(c.amount)}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "var(--gap-sm)", paddingTop: 8, borderTop: "1px solid var(--border)" }}>
              <span>Total Tagihan &amp; Potongan</span>
              <b style={{ color: costItemNetColor(costNet) }}>{fmtDelta(costNet)}</b>
            </div>
            {shouldShowBucketBars(txn) && (
              <div className="detail-sisa-box">
                <div className="detail-sisa-box__sisa">Sisa: Rp {fmtQty(tagihanPotonganSisa(txn))}</div>
                <PaymentProgress pct={tagihanPotonganPctPaid(txn)} />
              </div>
            )}
          </div>
        )}

        <div className="card" style={{ marginTop: "var(--gap-lg)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: status === "LUNAS" ? "var(--gap-sm)" : 0 }}>
            <span>Total Nilai</span><b style={{ color: totalColor }}>{fmtQty(total)}</b>
          </div>
          {status === "LUNAS" ? (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Sudah Dibayar</span><span style={{ color: "var(--green)" }}>{fmtQty(paid)}</span>
            </div>
          ) : (
            <div className="detail-sisa-box">
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "var(--gap-xs)" }}>
                <span>Sudah Dibayar</span><span style={{ color: "var(--green)" }}>{fmtQty(paid)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "var(--gap-xs)" }}>
                <span>Sisa Tagihan</span><span style={{ color: "#92400e" }}>{fmtQty(sisa)}</span>
              </div>
              <PaymentProgress pct={pctPaid(txn)} />
            </div>
          )}
          {(txn.payments || []).length > 0 && (
            <div style={{ marginTop: "var(--gap-sm)", paddingTop: 8, borderTop: "1px solid var(--border)" }}>
              {txn.payments.map((p) => (
                <div key={p.id}>
                  <div className="payment-row" style={{ display: "flex", justifyContent: "space-between", marginBottom: "var(--gap-xs)" }}>
                    <span>{paymentMethodText(p)} — {fmtDate(p.paidAt)}</span>
                    <span>Rp {fmtQty(p.amount)}</span>
                  </div>
                  {hasTagihanPotonganItems(txn) && (
                    <div style={{ fontSize: 12, color: "var(--muted)", marginTop: -2, marginBottom: "var(--gap-xs)" }}>{paymentSplitLine(p)}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {hasBiayaItems && (
          <div className="card" style={{ marginTop: "var(--gap-lg)" }}>
            <div className="card__title">Total Setelah Biaya</div>
            {biayaItems.map((c) => (
              <div key={c.id} style={{ display: "flex", justifyContent: "space-between", marginBottom: "var(--gap-sm)", color: "#475569" }}>
                <span>{c.description}</span>
                <span>Rp {fmtQty(c.amount)}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "var(--gap-sm)", paddingTop: 8, borderTop: "1px solid var(--border)" }}>
              <span>Total Biaya</span><span>{fmtQty(sumBiayaCostItems(costItems) + expenseTotal)}</span>
            </div>
            {!isPembelianView && (
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "var(--gap-sm)", paddingTop: 8, borderTop: "1px solid var(--border)" }}>
                <span>Total Dikurangi Biaya</span><b>{fmtQty(dikurangiBiaya)}</b>
              </div>
            )}
            {isPembelianView && (
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "var(--gap-sm)", paddingTop: 8, borderTop: "1px solid var(--border)" }}>
                <span>Total Ditambah Biaya</span><b>{fmtQty(ditambahBiaya)}</b>
              </div>
            )}
          </div>
        )}

        <div className="card">
          <div className="card__title">Riwayat Perubahan</div>
          <EditLogTimeline txn={txn} />
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-primary" onClick={onClose}>Tutup</button>
        </div>
      </div>
    </div>
  );
}
